
import { useState, useEffect } from "react";
import { Idea } from "@/components/IdeaCard";
import { useAuth } from "@/contexts/AuthContext";
import { historyService, migrateLocalStorageToDatabase } from "@/lib/historyUtils";
import { HistoryEntry } from "@/types/history";
import { safeGetFromStorage } from "@/utils/storageUtils";

export function useHistory() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [savedIdeas, setSavedIdeas] = useState<Idea[]>([]);
  
  useEffect(() => {
    async function loadData() {
      console.log("📚 useHistory: Starting to load data", { hasUser: !!user });
      setLoading(true);
      
      try {
        if (user) {
          // First check if there's localStorage data to migrate
          const localHistory = localStorage.getItem('createJoyHistory');
          const localSavedIdeas = localStorage.getItem('savedIdeas');
          
          if (localHistory || localSavedIdeas) {
            console.log("🔄 Migrating localStorage data to database...");
            await migrateLocalStorageToDatabase(user.id);
          }
          
          // Load data from database
          console.log("📊 Loading history from database...");
          const [historyData, savedIdeasData] = await Promise.all([
            historyService.getUserHistory(user.id),
            historyService.getUserSavedIdeas(user.id)
          ]);
          
          setHistory(historyData);
          setSavedIdeas(savedIdeasData);
          
          console.log(`✅ Successfully loaded ${historyData.length} history entries and ${savedIdeasData.length} saved ideas from database`);
        } else {
          // Fallback to localStorage for non-authenticated users
          console.log("👤 No user authenticated, using localStorage fallback");
          const { data: historyData } = safeGetFromStorage<HistoryEntry[]>("createJoyHistory", []);
          const { data: savedIdeasData } = safeGetFromStorage<Idea[]>("savedIdeas", []);
          
          setHistory(historyData);
          setSavedIdeas(savedIdeasData);
        }
      } catch (error) {
        console.error("💥 Error loading history data:", error);
        // Fallback to localStorage on database error
        if (user) {
          console.log("🔄 Database failed, falling back to localStorage");
          const { data: historyData } = safeGetFromStorage<HistoryEntry[]>("createJoyHistory", []);
          const { data: savedIdeasData } = safeGetFromStorage<Idea[]>("savedIdeas", []);
          
          setHistory(historyData);
          setSavedIdeas(savedIdeasData);
        } else {
          setHistory([]);
          setSavedIdeas([]);
        }
      } finally {
        setLoading(false);
        console.log("📚 useHistory: Data loading completed");
      }
    }
    
    loadData();
  }, [user]);

  const updateSavedIdeas = async (newSavedIdeas: Idea[]) => {
    setSavedIdeas(newSavedIdeas);
    // Note: Individual save/remove operations should be handled by components
    // This is just for state management
  };

  const deleteHistory = async (historyId: string) => {
    if (!user) {
      console.error('❌ Cannot delete history: User not authenticated');
      return;
    }

    try {
      // Call the service to delete from database
      await historyService.deleteHistoryEntry(user.id, historyId);
      
      // Update local state - remove the deleted entry
      setHistory(prevHistory => prevHistory.filter(entry => entry.id !== historyId));
      
      console.log(`✅ History entry ${historyId} deleted successfully`);
    } catch (error) {
      console.error('❌ Error deleting history entry:', error);
      throw error;
    }
  };

  return {
    loading,
    history,
    savedIdeas,
    setSavedIdeas: updateSavedIdeas,
    deleteHistory
  };
}

export default useHistory;
